
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarCheck2, RefreshCw, Search, ListChecks, Clock, AlertCircle } from 'lucide-react';
import { getAttendanceCardRows } from '../../store/slices/attendanceSlice';
import { normaliseRow } from './AttendanceCardView';

const CONCURRENCY = 6;

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function fmt(v) { return (!v || v === 'null') ? '' : String(v).trim(); }

const BUCKET_STYLE = {
  present: { label: 'Present', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  late:    { label: 'Late',    chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  absent:  { label: 'Absent',  chip: 'bg-red-50 text-red-700 border-red-200' },
  leave:   { label: 'Leave',   chip: 'bg-violet-50 text-violet-700 border-violet-200' },
  holiday: { label: 'Holiday', chip: 'bg-sky-50 text-sky-700 border-sky-200' },
  off:     { label: 'Day Off', chip: 'bg-gray-100 text-gray-500 border-gray-200' },
};

export default function TodayAttendancePanel({ employees, onSelectEmployee }) {
  const [results, setResults] = useState({}); // serviceNo -> { bucket, inTime, outTime } | null (no data)
  const [checked, setChecked] = useState(0);
  const [running, setRunning] = useState(false);
  const [search, setSearch] = useState('');
  const cancelledRef = useRef(false);
  const month = currentMonth();
  const today = new Date().getDate();

  const targets = useMemo(() => {
    return (employees || [])
      .map(e => ({
        serviceNo: fmt(e.HED_SERVICE_NO) || fmt(e.p_ced_service_no),
        name: fmt(e.HED_REPORT_NAME) || fmt(e.p_report_name) || `${fmt(e.HED_FIRST_NAME || e.p_first_name)} ${fmt(e.HED_LAST_NAME || e.p_last_name)}`.trim(),
        raw: e
      }))
      .filter(e => e.serviceNo);
  }, [employees]);

  const runScan = async () => {
    if (!targets.length) return;
    cancelledRef.current = false;
    setRunning(true);
    setChecked(0);
    setResults({});

    let idx = 0;
    async function worker() {
      while (idx < targets.length && !cancelledRef.current) {
        const my = targets[idx++];
        try {
          const rows = await getAttendanceCardRows({ serviceNo: my.serviceNo, month });
          const todayRow = rows.map(normaliseRow).find(r => r.day === today) || null;
          setResults(prev => ({ ...prev, [my.serviceNo]: todayRow }));
        } catch {
          setResults(prev => ({ ...prev, [my.serviceNo]: undefined })); // undefined = fetch failed
        }
        setChecked(c => c + 1);
      }
    }
    const workers = Array.from({ length: Math.min(CONCURRENCY, targets.length) }, worker);
    await Promise.all(workers);
    if (!cancelledRef.current) setRunning(false);
  };

  useEffect(() => {
    runScan();
    return () => { cancelledRef.current = true; };
    // eslint-disable-next-line
  }, [employees.length]);

  const rows = useMemo(() => {
    const q = search.toLowerCase().trim();
    return targets
      .filter(t => !q || t.name.toLowerCase().includes(q) || t.serviceNo.toLowerCase().includes(q))
      .map(t => ({ ...t, todayRow: results[t.serviceNo] }));
  }, [targets, results, search]);

  const stats = useMemo(() => {
    const c = { present: 0, late: 0, absent: 0, leave: 0, holiday: 0, off: 0 };
    Object.values(results).forEach(r => {
      if (r && c[r.bucket] !== undefined) c[r.bucket]++;
      else if (r === null) c.absent++;
    });
    return c;
  }, [results]);

  const todayLabel = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CalendarCheck2 size={16} className="text-slate-600" />
          <div>
            <p className="text-sm font-bold text-gray-800">Today's Attendance</p>
            <p className="text-xs text-gray-400">{todayLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name or service no..."
              className="pl-7 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-gray-50 focus:bg-white focus:outline-none focus:border-blue-400 w-52"
            />
          </div>
          <button
            onClick={runScan}
            disabled={running}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={12} className={running ? 'animate-spin' : ''} /> {running ? `Checking ${checked}/${targets.length}` : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Summary pills */}
      <div className="flex flex-wrap gap-2.5">
        {Object.entries(BUCKET_STYLE).map(([key, s]) => (
          <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold ${s.chip}`}>
            <span>{s.label}</span><span className="text-sm font-extrabold">{stats[key] || 0}</span>
          </div>
        ))}
        {running && (
          <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-xs text-gray-500">
            <ListChecks size={12} /> {checked}/{targets.length} employees checked
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-y-auto max-h-[420px]">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Service No</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">In</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide">Out</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((r, i) => {
                const tr = r.todayRow;
                const bucket = tr ? tr.bucket : (tr === undefined && !(r.serviceNo in results) ? null : 'absent');
                const s = bucket ? BUCKET_STYLE[bucket] : null;
                return (
                  <tr
                    key={r.serviceNo}
                    onClick={() => onSelectEmployee && onSelectEmployee(r.serviceNo)}
                    className={`cursor-pointer hover:bg-blue-50/50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                  >
                    <td className="px-3 py-2 font-mono text-blue-700 font-semibold whitespace-nowrap">{r.serviceNo}</td>
                    <td className="px-3 py-2 text-gray-800 font-medium whitespace-nowrap">{r.name || '—'}</td>
                    <td className="px-3 py-2">
                      {s ? (
                        <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${s.chip}`}>{s.label}</span>
                      ) : (
                        <span className="text-gray-300 text-[11px]">checking...</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{tr?.inTime || '—'}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{tr?.outTime || '—'}</td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">
                  <AlertCircle size={16} className="inline-block mr-1.5 opacity-50" /> No employees match your search
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
